import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { CommitOnEnterDirective } from 'src/app/shared/directives/commit-on-enter.directive';
import { SelectOnFocusDirective } from 'src/app/shared/directives/select-on-focus.directive';
import { TwoDecimalsDirective } from 'src/app/shared/directives/two-decimals.directive';
import { ConfirmationModalComponent } from 'src/app/shared/modals/confirmation-modal/confirmation-modal.component';
import { ConfirmationModalData } from 'src/app/shared/modals/confirmation-modal/modal-data.models';
import { ReallocationV2Store } from '../../state/reallocation-v2.store';

/**
 * Headings for the SMA dialogs.
 *
 * Two headings over three uses. The confirm heading covers moving INTO an SMA and removing one
 * -- both are questions the advisor answers, and both are the same fact about the account, so
 * the same heading is right even though the copy differs.
 *
 * The blocked heading is separate because that dialog does a different job: it reports that the
 * account cannot go where the advisor pointed it. A single neutral heading over a refusal reads
 * as though a choice is still on offer.
 */
const SMA_CONFIRM_TITLE = 'Confirm Separately Managed Account';

/**
 * Heading for the locked-down notice, which is neither a confirmation nor a refusal of something
 * the advisor picked -- it states what the account IS and what has to happen first.
 */
const SMA_LOCKED_TITLE = 'This account is a Separately Managed Account';
const SMA_BLOCKED_TITLE = 'This account can\'t use this model';

/**
 * Step 4 — split the amount going to models across them.
 *
 * Inputs are bound one-way from the store's derived `allocation()` view and push edits back
 * through store methods, so the store stays the single source of truth.
 *
 * ⚠️ **One box, one row (ACMTECH-969).** Nothing here rebalances siblings any more. The advisor
 * states the total going to models -- {@link ReallocationV2Store.modelPot} -- and each row's
 * dollars are `pot × target%`; an edit to a Target %, a Value or a split changes that row and
 * nothing else. The three percentage columns must still each total 100 to leave the step, but
 * getting them there is the advisor's, not the wizard's, which is what stopped one keystroke
 * from renumbering figures nobody had looked at.
 */
@Component({
  selector: 'app-rav2-model-allocation-step',
  standalone: true,
  // `appCommitOnEnter` and `appTwoDecimals` went with the pot boxes (ACMTECH-1039); the
  // grid inputs carry neither.
  imports: [CommonModule, SelectOnFocusDirective, CommitOnEnterDirective, TwoDecimalsDirective],
  templateUrl: './model-allocation-step.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModelAllocationStepComponent {
  readonly store = inject(ReallocationV2Store);
  private readonly dialog = inject(MatDialog);

  // ---- Raise Cash from Models (ACMTECH-1039, moved here from step 1) ---------
  /**
   * What the box shows. Blank at zero, so an untouched section looks untouched rather than
   * filled in with 0.00 -- the same rule the sell and cash boxes on step 1 follow.
   */
  freeAmountInput(): string {
    const amount = this.store.modelFreeAmount();
    return amount === 0 ? '' : amount.toFixed(2);
  }

  /**
   * Raise cash out of the models.
   *
   * ⚠️ ONE-WAY BINDING, NOT A FORM. This is an input to the allocation arithmetic, and its cap
   * and total are store computeds; a control would put the figure somewhere nothing reading it
   * can see without a `formVersion` bump.
   *
   * ⚠️ THE ACCEPTED FIGURE IS WRITTEN BACK. The store rounds and floors what it is given, so
   * when that lands on the figure already held, nothing in the signal graph changes, Angular
   * re-renders nothing, and the box goes on showing what was typed. The `onPot` lesson.
   */
  onFreeAmount(event: Event): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value.replace(/,/g, '').trim();
    const parsed = Number.parseFloat(raw);

    this.store.setModelFreeAmount(raw === '' || Number.isNaN(parsed) ? 0 : parsed);
    input.value = this.freeAmountInput();
  }

  /**
   * What the info icon beside the Raise Cash from Models heading says.
   *
   * 🔴 THE ONE THING ABOUT THIS SECTION AN ADVISOR WOULD GET WRONG. The amount is entered once
   * for the whole section, so the natural reading is that it comes out of the models evenly, or
   * out of whichever one has the most. It comes out of every sleeve in proportion to its TARGET
   * -- which is what leaves the account on its stated allocation afterwards (trade team,
   * 2026-10-29), and the reason there is no box per model to argue with.
   *
   * ⚠️ It states the CAP as well, because the two are one rule: the raise is spread by target,
   * so the sleeve whose target reaches its minimum first limits the whole amount -- which is
   * otherwise an inexplicable ceiling.
   *
   * Held here rather than in the markup because it is bound four times, on the heading, the
   * icon, the icon's `aria-label` and the hint under the box.
   */
  readonly managedModelsTooltip =
    'Cash raised from the models is taken across every sleeve in proportion to its target '
    + 'allocation, not from any one model. The amount is limited so that no model is left '
    + 'below its investment minimum.';

  /**
   * Why the amount box is disabled. Bound twice, so held rather than built per call.
   *
   * Names Reset deliberately: it is the only way back, and a disabled box with no route out of
   * it reads as a broken screen rather than a locked one.
   */
  readonly raiseLockedTooltip =
    'Model values have been set individually below, which raises its own cash. Reset the '
    + 'allocation to raise cash by target percentage instead.';

  /**
   * Why the amount box is disabled when there is simply no headroom.
   *
   * ⚠️ A DIFFERENT STATEMENT FROM THE LOCK, and worth its own sentence: nothing the advisor did
   * caused this one. Every model is already at the investment minimum it has to stay above, so
   * there is no cash in them to raise -- and the remedy is to remove a model rather than to undo
   * anything.
   *
   * Shown as the hint under the box as well as on the icon, so a greyed control is never
   * unexplained.
   */
  readonly raiseUnavailableTooltip =
    'No cash can be raised from the models -- each one is already at the investment minimum it '
    + 'has to stay above. Remove a model below to free its balance.';

  fmtPct(value: number): string {
    return value.toFixed(2);
  }

  /**
   * A percentage for an INPUT box — blank at zero.
   *
   * Since ACMTECH-945 the grid opens with nothing allocated, so every percentage derives from
   * values that are not there yet and reads 0. Showing "0.00" in seven boxes makes an untouched
   * grid look filled in and wrong; blank makes it look like what it is, waiting for input.
   *
   * Distinct from {@link fmtPct}, which still formats the TOTALS row — a total of zero is a
   * real figure and belongs on screen.
   */
  pctInput(value: number): string {
    return value === 0 ? '' : value.toFixed(2);
  }

  /**
   * A dollar figure for a money input: grouped to thousands and held to two decimals.
   *
   * `203280.61` -> `203,280.61`. The separators are what make a six-figure pot scannable; the
   * boxes they go in are `type="text" inputmode="decimal"` precisely so they can carry one (a
   * `type="number"` input's value sanitiser rejects any string that is not a plain float, so
   * assigning a grouped figure there EMPTIES the box).
   *
   * ⚠️ Every parse of these boxes must strip the separators first, or `parseFloat` stops at the
   * comma and reads `203,280.61` as 203. {@link onPot} and {@link onValue} both do.
   *
   * ⚠️ Deliberately NOT the `currency` pipe. The `$` sits outside the box on every money input
   * in this wizard, and a symbol inside the field would have to be parsed back out of anything
   * the advisor typed.
   */
  private money(value: number): string {
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /**
   * An allocated value for its input box — blank at zero.
   *
   * A zero row is never valid (the advisor funds it or removes it), so there is no state this
   * hides: blank and zero mean the same thing here, and blank is the honest rendering of a box
   * nobody has typed in.
   *
   * ⚠️ The figure it shows is DERIVED (`pot × target%`), so a typed amount comes back rounded
   * to the cent and, when the pot is small enough for a hundredth to matter, a cent off what
   * was typed. That is the pot's arithmetic being honest rather than the box remembering an
   * amount that no longer follows from anything.
   */
  valueInput(value: number): string {
    return value === 0 ? '' : this.money(value);
  }

  private num(event: Event): number {
    return parseFloat((event.target as HTMLInputElement).value.replace(/,/g, '')) || 0;
  }

  /**
   * Commit a model selection — after the SMA questions, when there are any.
   *
   * Intercepted in the COMPONENT rather than in `selectModel`, because confirm-then-commit
   * is asynchronous and the store is synchronous and has no MatDialog. The store still owns
   * the decision and the copy ({@link ReallocationV2Store.smaSelectionCheck}); this owns
   * only the dialog and the revert.
   *
   * This is the single funnel for the dropdown, so there is no second path an SMA could be
   * chosen through.
   */
  onModel(id: string, event: Event): void {
    const select = event.target as HTMLSelectElement;
    const modelId = select.value;
    const check = this.store.smaSelectionCheck(modelId);

    if (check.kind === 'none') {
      this.store.selectModel(id, modelId);
      return;
    }

    if (check.kind === 'blocked') {
      this.revertSelection(select, id);
      // No Cancel, and no warning red: there is nothing to decide and nothing has gone
      // wrong -- the selection simply did not take, and this says why.
      this.openConfirmDialog({
        title: SMA_BLOCKED_TITLE,
        message: check.message,
        hideCancelButton: true,
        confirmationButtonText: 'OK',
      });
      return;
    }

    this.openConfirmDialog({
      title: SMA_CONFIRM_TITLE,
      message: check.message,
      question: check.question,
      confirmationButtonText: 'Confirm',
    })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) this.store.commitToSma(modelId);
        else this.revertSelection(select, id);
      });
  }

  /**
   * Add a model row -- unless an SMA already holds the account, in which case say so.
   *
   * ⚠️ The button is dimmed by CLASS, not by the `disabled` attribute, so the click still reaches
   * this method. That is the point: a truly disabled control cannot explain itself, and "the
   * button is grey" is not an answer to "why can I not add a model". Same convention the account
   * request modal's nav buttons use, and like them this re-checks the condition rather than
   * trusting the styling.
   */
  onAddModel(): void {
    if (this.store.smaLockActive()) {
      this.openConfirmDialog({
        title: SMA_LOCKED_TITLE,
        message: this.store.smaLockMessage(),
        hideCancelButton: true,
        confirmationButtonText: 'OK',
      });
      return;
    }

    this.store.addRow();
  }

  /**
   * Drop a model row -- after confirmation when the row holds an SMA.
   *
   * Intercepted in the COMPONENT for the same reason `onModel` is: confirm-then-act is
   * asynchronous, the store is synchronous and has no MatDialog. The store owns the decision
   * and the copy ({@link ReallocationV2Store.smaRemovalPrompt}); this owns only the dialog.
   *
   * 🟢 No revert branch, unlike `onModel`. Nothing has changed yet when the question is asked
   * -- the click does not remove the row, this does -- so cancelling means doing nothing rather
   * than undoing something.
   */
  onRemove(rowId: string): void {
    const prompt = this.store.smaRemovalPrompt(rowId);

    if (prompt === null) {
      this.store.removeRow(rowId);
      return;
    }

    this.openConfirmDialog({ title: SMA_CONFIRM_TITLE, ...prompt, confirmationButtonText: 'Confirm' })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) this.store.removeRow(rowId);
      });
  }

  /**
   * Open one of the SMA dialogs at a readable width.
   *
   * ⚠️ The width is not decoration. `mat-dialog-container` has no width of its own — only
   * `max-w-full` — so the panel sizes to its content, and a paragraph's max-content width is
   * the whole unwrapped paragraph. Every other confirmation in the portal is a single short
   * sentence, so this never showed up; these messages run to a few hundred characters and
   * stretched the panel across the viewport.
   *
   * 56rem is 560px against this app's 10px root, which is roughly 60 characters a line at
   * `text-lg` (16px) — a normal measure to read. `maxWidth` keeps it inside a narrow screen,
   * where 560px would otherwise overflow.
   */
  private openConfirmDialog(data: ConfirmationModalData) {
    return this.dialog.open<ConfirmationModalComponent, ConfirmationModalData, boolean>(
      ConfirmationModalComponent,
      { width: '56rem', maxWidth: '90vw', data },
    );
  }

  /**
   * Put the dropdown back on the row's actual model.
   *
   * ⚠️ Explicitly, on the element. The `<option [selected]>` bindings read `row.modelId`,
   * which never changed — the browser moved the select on its own — so leaving the store
   * untouched leaves the DOM showing a model the row does not hold. Nothing in Angular
   * re-runs to correct that, because from its side nothing happened.
   */
  private revertSelection(select: HTMLSelectElement, rowId: string): void {
    select.value = this.store.allocation().rows.find((r) => r.id === rowId)?.modelId ?? '';
  }

  onTarget(id: string, event: Event): void {
    this.store.editTarget(id, this.num(event));
  }

  onDistribution(id: string, event: Event): void {
    this.store.editDistribution(id, this.num(event));
  }

  onContribution(id: string, event: Event): void {
    this.store.editContribution(id, this.num(event));
  }

  /**
   * Why the value boxes are disabled, on every row that shows one.
   *
   * Held as a field rather than built per call: it is bound twice on each row -- title and
   * aria-label -- so a getter would rebuild it on every change-detection pass.
   */
  readonly valuesLockedTooltip =
    'Amount to Raise above is already raising money from the models by target percentage. '
    + 'Clear that amount to set model values individually instead.';

  onValue(id: string, event: Event): void {
    const raw = (event.target as HTMLInputElement).value.replace(/,/g, '');
    const parsed = parseFloat(raw);
    // Emptying the box means NOTHING allocated to this row -- not "revert to the
    // percentage-derived figure", which is what clearing the cap used to do.
    //
    // That fallback made sense while the column was an override on top of a derived value.
    // Since ACMTECH-944 the grid OPENS at what each sleeve holds, so falling back reverted
    // the row to absorbing the whole investable amount: the box read empty, the total read
    // the full figure, and Next stayed enabled. Zero is the honest reading of an empty box,
    // and it leaves the money unapplied so the gate can catch it.
    this.store.editValue(id, raw === '' || isNaN(parsed) ? 0 : parsed);
  }

  // 🔴 `onPot`, `potInput`, `onPotPercent` and `potPercentInput` were HERE and are gone with
  // the boxes they drove (ACMTECH-1039). The two figures in the grey bar are read-only now,
  // bound to `alloc.totals.value` and `allocatedPercent()`.
  //
  // Deleted rather than left unused, deliberately: a handler that can still write the pot is
  // the "second, implicit pot by some other route" that `modelPotSignal` is documented to
  // guard against, and an unreachable one is worse than a reachable one because nothing
  // exercises it. `setModelPot` itself stays -- it is the single writer, and `useAllInvestable`
  // and `commitToSma` go through it.

  /**
   * What applying the remainder would move: `investable − pot`.
   *
   * ⚠️ Against the POT, not `unappliedValue`. The two agree whenever the target column totals
   * 100 -- which is every state the step can be left in -- and diverge only mid-edit, where the
   * rows split the pot into something other than the pot. It is the pot this control sets, so
   * this is the figure that is exactly closed by one click; keying the button on the unapplied
   * figure would leave it offering an amount it cannot fully place.
   */
  private remaining(): number {
    return this.store.investable() - this.store.modelPot();
  }

  /**
   * What share of the whole account the model rows come to.
   *
   * Derived and never stored, exactly as the percentage box it replaced was: two stored
   * figures for one amount disagree the moment anything else moves the money.
   *
   * ⚠️ OF THE ACCOUNT, not of what is investable, so it reads against TOTAL ACCOUNT VALUE in
   * the strip above rather than against a figure that is not on screen.
   */
  allocatedPercent(): number {
    const total = this.store.totalValue();
    return total > 0 ? (this.store.allocation().totals.value / total) * 100 : 0;
  }

  /**
   * Allocate Remaining, asking first when it would undo Raise Cash.
   *
   * 🔴 ONE STORE CALL ON ACCEPT, never clear-then-allocate from here. `useAllInvestable` reads
   * `investable`, which INCLUDES whatever Raise Cash freed, so allocating first and
   * clearing second would set the pot from a figure the clear is about to withdraw and leave
   * the request over-committed on a click the advisor had just approved. The store does both,
   * in the order that works.
   *
   * ⚠️ Asked HERE and not inside `canApplyRemaining`. That governs VISIBILITY, and gating it on
   * the warning would hide the control rather than let it explain itself -- the inverse of the
   * rule this step already follows for the SMA-locked Add Model button.
   *
   * No revert branch: nothing has been written when the question is asked, so Cancel is doing
   * nothing. Same shape as `onRemove`.
   */
  onAllocateRemaining(): void {
    const check = this.store.allocateRemainingCheck();

    if (check.kind === 'proceed') {
      this.store.useAllInvestable();
      return;
    }

    this.openConfirmDialog({
      // Not `isWarning`: that paints the solid red reserved for destructive actions, and this
      // is a question about something the advisor has just asked for. The SMA confirmations,
      // which are the same shape, do not use it either.
      title: 'This will undo what you raised from your models',
      message: check.message,
      question: check.question,
      confirmationButtonText: 'Allocate Remaining',
    })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) this.store.useAllInvestable();
      });
  }

  /**
   * Whether the control has anything to do.
   *
   * ⚠️ NOT gated on the grid being sound, which its pro-rata predecessor had to be: that one
   * divided money across the rows, so it needed a model on every row and a target column on
   * 100, and was therefore unavailable exactly when a fresh grid needed funding. This one moves
   * the total and touches no row, so there is nothing about the split it could get wrong.
   */
  canApplyRemaining(): boolean {
    return Math.abs(this.remaining()) >= 0.005;
  }

  /**
   * What the control says.
   *
   * ⚠️ NO AMOUNT. It read `Apply Remaining $114,119.36`, and the figure made the button the
   * widest thing in the panel -- wider than the field it acts on, and re-flowing the row on every
   * keystroke as the remainder changed. The amount moved to the info icon beside it, where it can
   * be read on demand and cannot resize anything.
   *
   * Still names the DIRECTION, because the same control resolves an unplaced balance and an
   * over-commitment and those are opposite operations -- "Allocate Remaining" on a button that
   * was about to REDUCE the total would be a lie.
   */
  remainingLabel(): string {
    return this.remaining() > 0 ? 'Allocate Remaining' : 'Remove Excess';
  }

  /**
   * The sentence behind the info icon, and the button's own accessible name.
   *
   * ⚠️ This is where the amount lives now. It carries what the label used to: the figure being
   * moved, not the total it lands on -- the delta is what the advisor is already reading off Left
   * to allocate, and it is what makes the click predictable.
   *
   * Doubling as `aria-label` on the button is deliberate: a `title` on a decorative icon is
   * announced inconsistently, and a screen reader hearing only "Allocate Remaining" would be
   * told less than a sighted advisor who can hover.
   */
  remainingHint(): string {
    const remaining = this.remaining();
    const money = Math.abs(remaining).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    return remaining > 0
      ? `Allocate remaining ${money} to total model amount.`
      : `Remove ${money} of excess from the total model amount.`;
  }

  /** True when the outstanding amount is an over-commitment rather than money still to place. */
  isOverAllocated(): boolean {
    return this.store.unappliedValue() < 0;
  }

  isOffTarget(sum: number): boolean {
    return Math.abs(sum - 100) >= 0.005;
  }

  /**
   * How far a percentage column is from 100, as a signed figure in parentheses.
   *
   * Signed rather than the words "over" / "under". Direction is carried by the sign and by
   * the colour (see {@link deltaClass}), and parentheses keep it reading as an annotation on
   * the total above rather than as a second, competing number. It also stays short enough
   * not to wrap under a narrow column, which the word form did.
   */
  deltaLabel(delta: number): string {
    return `(${this.sign(delta)}${Math.abs(delta).toFixed(2)}%)`;
  }

  /**
   * How far this sleeve has drifted from its stated target, in percentage points -- `+1.23%`
   * when it holds a bigger slice of the model money than its target claims, `(1.23%)` when it
   * holds a smaller one, and '' when there is nothing to say.
   *
   * ⚠️ THE ONLY statement of the drift now. A `driftLabel` / `isOverfunded` pair said the same
   * thing in dollars on the model name's reference line, and both are gone: it is this figure
   * multiplied by the pot, and an advisor restating a mix is working in percentages -- the
   * dollars are what the wizard computes for them. One fact, in the column whose question it
   * answers. {@link AllocationRow.sleeveDrift} still carries the dollar form if it is wanted
   * back; the markup for it is in git.
   *
   * ⚠️ A figure, not the words OVERFUNDED / UNDERFUNDED this pair used to read. The words needed
   * a pill to hold them and the pill needed a colour, and between them they drowned the figure
   * they were annotating. A signed delta says the same thing in a quarter of the space, and it
   * reads as what it is: a difference. The words survive in the `title`, which is also what
   * carries the meaning to a screen reader, since neither a sign nor a bracket speaks for itself.
   *
   * ⚠️ Standard financial convention, which is the only reason to pick one direction over the
   * other: PARENTHESES MEAN NEGATIVE. `actual share − stated target`, so an overfunded sleeve is
   * the positive figure and takes the `+`, and an underfunded one is the negative figure and
   * takes the brackets. Reversing that -- `+` for the shortfall, on the reading that `+` means
   * "add this much" -- was considered and rejected: it makes the sign describe the remedy while
   * the colour describes the state, and a reader who knows the convention would take a bracketed
   * figure for a surplus.
   *
   * Returning the empty string rather than null is deliberate: the template can then use
   * `@if (actualPctDeltaLabel(...); as gap)` and get the guard and the text from one call,
   * instead of testing one expression and rendering another that could drift from it.
   *
   * '' when either figure is missing -- an advisor-added row has neither -- or when the gap is
   * under half a basis point, where "+0.00%" would draw the eye to the one row that is fine.
   */
  actualPctDeltaLabel(actual: number | null | undefined, target: number | null | undefined): string {
    if (actual == null || target == null) return '';
    const delta = actual - target;
    if (Math.abs(delta) < 0.005) return '';
    return delta > 0 ? `+${delta.toFixed(2)}%` : `(${Math.abs(delta).toFixed(2)}%)`;
  }

  /**
   * Whether the sleeve's actual share EXCEEDS its stated target -- the colour and the wording.
   *
   * ⚠️ NOT `isOverAllocated`, which already exists on this component and means something else
   * entirely: that the REQUEST commits more than the account has. This is about one sleeve's
   * share of today's model money against its own target, and confusing the two would put the
   * over-commitment colour on a perfectly ordinary row.
   */
  holdsMoreThanTarget(actual: number | null | undefined, target: number | null | undefined): boolean {
    return actual != null && target != null && actual - target > 0;
  }

  /**
   * A true minus sign, not a hyphen — matching the − already used elsewhere in this wizard.
   *
   * Only called for deltas that {@link isOffTarget} / {@link isOffValue} have already found
   * non-zero, so there is no third case to render.
   */
  private sign(delta: number): string {
    return delta > 0 ? '+' : '−';
  }

  /**
   * Colour for the over/under line: red when over, mint when under.
   *
   * The two are not the same kind of problem. Over means the advisor has committed more
   * than exists and something must come back out. Under means there is simply more still
   * to place — a normal mid-edit state, not a mistake. Painting both red would flag the
   * ordinary act of typing as an error.
   *
   * `mint-darkest` rather than a lighter mint: on this near-white row the lighter tokens
   * do not carry enough contrast to read as text.
   */
  deltaClass(delta: number): string {
    return delta > 0 ? 'text-red-dark' : 'text-mint-darkest';
  }
}
